// frontend/src/pages/mentor/MentorDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import { 
    UserGroupIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CalendarIcon,
    CheckCircleIcon,
    VideoCameraIcon
} from '@heroicons/react/24/outline';
import EmptyState from '../../components/common/EmptyState';
import StatusBadge from '../../components/common/StatusBadge';

const MentorDashboard = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/mentor/dashboard');
            setData(response.data.data);
        } catch (error) {
            toast.error('Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveHour = async (hourId, comments = '') => {
        try {
            await axios.post(`/mentor/hours/${hourId}/approve`, { comments });
            toast.success('Hour entry approved');
            loadDashboard();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to approve');
        }
    };

    const handleReturnHour = async (hourId, comments) => {
        if (!comments) {
            toast.error('Comments are required when returning hours');
            return;
        }
        try {
            await axios.post(`/mentor/hours/${hourId}/return`, { comments });
            toast.success('Hour entry returned for changes');
            loadDashboard();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to return');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-72" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="skeleton h-24" />
                    ))}
                </div>
                <span className="sr-only">Loading dashboard…</span>
            </div>
        );
    }

    const { assignedInterns, stats, pendingActions, alerts, upcomingSessions } = data || {};

    return (
        <div className="space-y-6 pb-16">
            {/* Header */}
            <div className="animate-fade-up">
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-700">Mentor Workspace</span>
                <h1 className="font-serif text-3xl font-bold text-ink-950 mt-1">Mentor Command Center</h1>
                <p className="text-sm text-ink-600 mt-1">
                    Welcome back, {user?.first_name || 'Mentor'}. Review assigned interns, pending hours, and upload video lectures.
                </p>
            </div>

            {/* Quick Action Banner */}
            <div className="bg-surface border border-brand-200/80 rounded-2xl p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-up">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center text-brand-800 shrink-0">
                        <VideoCameraIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="font-serif text-lg font-bold text-ink-950">Add & Manage Video Lectures</h2>
                        <p className="text-xs text-ink-600">Assign training modules to your interns and set required pass percentages.</p>
                    </div>
                </div>
                <Link
                    to="/mentor/learning-modules"
                    className="inline-flex items-center justify-center bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors shadow-xs"
                >
                    + Upload Video Lecture
                </Link>
            </div>

            {/* KPI Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-up">
                    <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs text-left">
                        <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Total Interns</span>
                        <p className="font-serif text-3xl font-bold text-ink-950 mt-2">{stats.totalInterns || 0}</p>
                    </div>
                    <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs text-left">
                        <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Active</span>
                        <p className="font-serif text-3xl font-bold text-brand-700 mt-2">{stats.activeInterns || 0}</p>
                    </div>
                    <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs text-left">
                        <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Hours Approved</span>
                        <p className="font-serif text-3xl font-bold text-ink-950 mt-2">{Math.round(stats.totalApprovedHours || 0)}</p>
                    </div>
                    <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs text-left">
                        <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Pending Hours</span>
                        <p className="font-serif text-3xl font-bold text-amber-700 mt-2">{Math.round(stats.totalPendingHours || 0)}</p>
                    </div>
                </div>
            )}

            {/* Alerts */}
            {alerts && alerts.length > 0 && (
                <div className="rounded-2xl border border-red-200 bg-red-50/80 p-5 shadow-xs animate-fade-up">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 shrink-0" aria-hidden="true" />
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-red-900">
                                {alerts.length} Alert{alerts.length > 1 ? 's' : ''} requiring attention
                            </h3>
                            <div className="mt-2 space-y-1">
                                {alerts.slice(0, 3).map((alert) => (
                                    <div key={alert.id} className="text-sm text-red-800">
                                        {alert.message}
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await axios.put(`/mentor/alerts/${alert.id}/resolve`);
                                                loadDashboard();
                                            }}
                                            className="ml-2 text-xs font-semibold text-red-900 hover:underline"
                                        >
                                            Resolve
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-xs animate-fade-up">
                <div className="border-b border-border bg-canvas/40 px-4 pt-2">
                    <nav className="flex space-x-2" aria-label="Tabs">
                        {[
                            { id: 'overview', label: 'Overview', icon: UserGroupIcon },
                            { id: 'actions', label: 'Pending Actions', icon: ClockIcon },
                            { id: 'sessions', label: 'Sessions', icon: CalendarIcon },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 rounded-t-lg ${
                                    activeTab === tab.id
                                        ? 'border-brand-700 text-brand-900 bg-surface font-semibold shadow-xs'
                                        : 'border-transparent text-ink-600 hover:text-ink-950'
                                }`}
                            >
                                <tab.icon className="w-4 h-4" aria-hidden="true" />
                                <span>{tab.label}</span>
                                {tab.id === 'actions' && pendingActions?.length > 0 && (
                                    <span className="px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-900 rounded-full">
                                        {pendingActions.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-5 md:p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-serif text-xl font-bold text-ink-950">Assigned Interns</h3>
                                <Link to="/mentor/interns" className="text-xs font-bold text-brand-700 hover:underline">View All →</Link>
                            </div>
                            {!assignedInterns || assignedInterns.length === 0 ? (
                                <EmptyState title="No assigned interns" description="You have not been assigned any interns yet." />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {assignedInterns.map((intern) => (
                                        <div key={intern.id} className="p-4 border border-border rounded-xl bg-canvas/30 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-semibold text-ink-950">{intern.first_name} {intern.last_name}</p>
                                                    <p className="text-xs text-ink-500">{intern.email}</p>
                                                </div>
                                                <StatusBadge status={intern.status || 'active'} />
                                            </div>
                                            <div className="text-xs text-ink-600 flex justify-between pt-2 border-t border-border/40">
                                                <span>Hours logged: {intern.approved_hours || 0} hrs</span>
                                                <Link to={`/mentor/interns/${intern.id}`} className="font-semibold text-brand-700 hover:underline">Details →</Link>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'actions' && (
                        <div className="space-y-4">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Pending Hours Approvals</h3>
                            {!pendingActions || pendingActions.length === 0 ? (
                                <EmptyState title="No pending approvals" description="All submitted intern hours have been reviewed." />
                            ) : (
                                <div className="space-y-3">
                                    {pendingActions.map((action) => (
                                        <div key={action.id} className="p-4 border border-border rounded-xl bg-surface flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-2xs">
                                            <div>
                                                <p className="font-semibold text-ink-950">{action.internName}</p>
                                                <p className="text-xs text-ink-600">{action.date} · {action.hours} hrs · <span className="font-medium text-ink-800">{action.category}</span></p>
                                                {action.description && <p className="text-xs text-ink-500 mt-1">{action.description}</p>}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleApproveHour(action.id)}
                                                    className="px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-lg transition-colors"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleReturnHour(action.id, 'Please add more description')}
                                                    className="px-3.5 py-1.5 border border-border text-ink-700 hover:bg-canvas text-xs font-semibold rounded-lg transition-colors"
                                                >
                                                    Return
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'sessions' && (
                        <div className="space-y-4">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Upcoming Mentor Sessions</h3>
                            {!upcomingSessions || upcomingSessions.length === 0 ? (
                                <EmptyState title="No upcoming sessions" description="No scheduled 1-on-1 check-ins for today." />
                            ) : (
                                <div className="space-y-3">
                                    {upcomingSessions.map((session) => (
                                        <div key={session.id} className="p-4 border border-border rounded-xl bg-canvas/30 flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold text-ink-950">{session.title}</p>
                                                <p className="text-xs text-ink-500">{session.scheduled_at}</p>
                                            </div>
                                            <span className="px-2.5 py-1 text-xs font-semibold bg-brand-100 text-brand-900 rounded-full">Scheduled</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MentorDashboard;
